import React from 'react';
import { ColumnActionsMode, IColumn } from '@fluentui/react';
import { Button, Tooltip } from '@fluentui/react-components';
import { CheckmarkCircleColor } from '@fluentui/react-icons';
import { IServiceContact } from '../../companies/serviceContacts/interfaces';
import { IEntityTerminationTask } from '../types';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';
import EntityTerminationDocumentIndicator from './EntityTerminationDocumentIndicator';

const truncatedCellStyle: React.CSSProperties = {
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const taskDescriptionContainerStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  minWidth: 0,
  width: '100%',
};

const taskDescriptionButtonStyle: React.CSSProperties = {
  flexShrink: 1,
  justifyContent: 'start',
  minWidth: 0,
  overflow: 'hidden',
  padding: 0,
};

const completedStatusStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'inline-flex',
  minHeight: '32px',
};

const onRenderTaskDescription = (
  item: IEntityTerminationTask,
  toggleEditTaskPanel: (item: IEntityTerminationTask) => void,
  showDocumentIndicator: boolean,
): JSX.Element => {
  return (
    <span style={taskDescriptionContainerStyle}>
      <Tooltip withArrow content={item?.description} relationship="label">
        <Button
          appearance="transparent"
          style={taskDescriptionButtonStyle}
          onClick={() => toggleEditTaskPanel(item)}
        >
          <span style={truncatedCellStyle}>{item?.description}</span>
        </Button>
      </Tooltip>
      {showDocumentIndicator && item.documents && item.documents.length > 0 && (
        <EntityTerminationDocumentIndicator />
      )}
    </span>
  );
};

const onRenderCompletedStatus = (item: IEntityTerminationTask): JSX.Element => {
  return (
    <span style={completedStatusStyle}>
      {item.completedDate && <CheckmarkCircleColor fontSize={20} />}
    </span>
  );
};

const getColumns = (
  toggleEditTaskPanel: (item: IEntityTerminationTask) => void,
  showDocumentIndicator = false,
): Array<IColumn> => [
  {
    key: 'completed',
    name: 'Completion Status',
    fieldName: 'completed',
    minWidth: 30,
    maxWidth: 30,
    isResizable: false,
    onRender: (item) => onRenderCompletedStatus(item),
  },
  {
    key: 'taskDescription',
    name: 'Task Description',
    fieldName: 'description',
    minWidth: 300,
    maxWidth: 300,
    isResizable: !toggleEditTaskPanel,
    columnActionsMode: toggleEditTaskPanel ? undefined : ColumnActionsMode.disabled,
    onRender: (item) => onRenderTaskDescription(item, toggleEditTaskPanel, showDocumentIndicator),
  },
  {
    key: 'owner',
    name: 'Owner',
    fieldName: 'owner',
    minWidth: 150,
    maxWidth: 150,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const owner = item.owner as IServiceContact;
      const display = Array.isArray(owner) ? owner.map((o) => o.name).join(', ') : owner?.text;
      return <span>{display}</span>;
    },
  },
  {
    key: 'additionalTask',
    name: 'Additional Task',
    fieldName: 'comment',
    minWidth: 300,
    maxWidth: 300,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const { comment: additionalTask } = item;
      if (!additionalTask) {
        return <span style={truncatedCellStyle} />;
      }
      return (
        <Tooltip withArrow content={additionalTask} relationship="label">
          <span style={truncatedCellStyle}>{additionalTask}</span>
        </Tooltip>
      );
    },
  },
  {
    key: 'assignee',
    name: 'Assignee(s)',
    fieldName: 'assignees',
    minWidth: 150,
    maxWidth: 150,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const field = item.assignees ?? item.assignee;
      const display = Array.isArray(field) ? field.map((o) => o.name).join(', ') : field?.text;

      return <span>{display}</span>;
    },
  },
  {
    key: 'followUpFlagDate',
    name: 'Follow Up Flag Date',
    fieldName: 'followUpFlagDate',
    minWidth: 150,
    maxWidth: 150,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const { followUpFlagDate } = item;
      const formattedDate = followUpFlagDate
        ? momentUtc(followUpFlagDate, StatDateFormats.TwoDigitDate)
        : '';
      return <span>{formattedDate}</span>;
    },
  },
  {
    key: 'completedDate',
    name: 'Completed Date',
    fieldName: 'completedDate',
    minWidth: 120,
    maxWidth: 120,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const { completedDate } = item;
      const formattedDate = completedDate
        ? momentUtc(completedDate, StatDateFormats.TwoDigitDate)
        : '';
      return <span>{formattedDate}</span>;
    },
  },
];

export default getColumns;
